import { createContext, useContext, useState, useMemo, useReducer, useEffect } from "react"
import moment from "moment";

import { IAppProviderProps } from "providers/AppProvider/AppProviderTypes";
import { REPORTS_INITIAL_CONTEXT, REPORTS_INITIAL_STATE } from "./ReportsProviderConstants";
import { IReportsContext } from "./ReportsProviderTypes";
import useGetReport from "api/querries/report/useGetReport";
import { IGetReportQueryParams } from "types/report";
import useGetReportStatuses from "api/querries/report/useGetReportStatuses";
import { useLocation } from "react-router-dom";
import useGetReportSources from "api/querries/report/useGetReportSources";
import useGetReportBoxes from "api/querries/report/useGetReportBoxes";
import { IRoles } from "types/shared";
import { useAppContext } from "providers/AppProvider/AppProvider";
import { useLaundryContext } from "providers/LaundryProvider/LaundryProvider";
import { useUI } from "providers/UIProvider";
import useGetReportWashers from "api/querries/report/useGetWashersForFilter";
import useGetReportPreview from "api/querries/report/useGetReportPreview";




const ReportsContext = createContext<IReportsContext>(REPORTS_INITIAL_CONTEXT);

export const useReportsContext: () => IReportsContext = () => useContext(ReportsContext)

const ReportsProvider = ({ children }: IAppProviderProps) => {
  const { pathname } = useLocation()
  const { userRole } = useAppContext()
  const { laundry } = useLaundryContext()
  const { lang } = useUI()
  const fetchReportsData = pathname === "/reports"

  const isQappUser = userRole && [IRoles.QAPP_ADMIN, IRoles.QAPP_MANAGER].includes(userRole);

  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    REPORTS_INITIAL_STATE,
  )


  const [query, setQuery] = useState<IGetReportQueryParams>({
    fromDate: null,
    toDate: null,
    carWashIds: [],
    statusIds: [],
    boxIds: [],
    washerIds: [],
    reservationSourceId: 0
  })

  const [previewQuery, setPreviewQuery] = useState<IGetReportQueryParams>({
    fromDate: null,
    toDate: null,
    carWashIds: [],
    statusIds: [],
    boxIds: [],
    washerIds: [],
    reservationSourceId: 0
  })


  const { data: reportData, refetch: refetchReportData, } = useGetReport({
    query,
    enabled: Boolean(query.fromDate && query.toDate)
  })

  const { data: reportPreviewData, refetch: refetchReportPreviewData } = useGetReportPreview({
    previewQuery,
    enabled: Boolean(previewQuery.fromDate && previewQuery.toDate)
  })


  const [reportInfo, setReportInfo] = useState(reportData)

  const { data: reportStatusesData, isFetched: isReportStatusesFetched, refetch: refetchReportStatuses } = useGetReportStatuses(fetchReportsData)
  const { data: reportSourcesData, isFetched: isReportSourcesFetched, refetch: refetchReportSources } = useGetReportSources(fetchReportsData)
  // const { data: reportBoxesData } = useGetReportBoxes(laundry?.id || 0, (Boolean(!isQappUser) && Boolean(laundry?.id)) || (Boolean(isQappUser) && state.selectedBranchesForReports.length !== 0))
  const { mutate: getReportBoxes, data: reportBoxesData } = useGetReportBoxes()
  const { mutate: getReportWashers, data: reportWashersData } = useGetReportWashers()


  useEffect(() => {
    setState({ reportPreview: reportPreviewData?.data })
  }, [reportPreviewData])

  useEffect(() => {
    if (!isQappUser && laundry?.id) {
      getReportBoxes([laundry?.id])
    }

    if (isQappUser) {
      getReportBoxes(state?.selectedBranchesForReports)
    }

  }, [laundry?.id, state?.selectedBranchesForReports])

  useEffect(() => {
    if (!isQappUser && laundry?.id) {
      getReportWashers([laundry?.id])
    }

    if (isQappUser) {
      getReportWashers(state?.selectedBranchesForReports)
    }
  }, [laundry?.id, state?.selectedBranchesForReports])

  useEffect(() => {
    setState({ selectedWashersForReports: [] })
    setState({ selectedBoxes: [] })
  }, [state?.selectedBranchesForReports])



  const handleDateChange = (dates: Date[]) => {

    setState({ reportDates: dates })
  }

  const generateReport = (fromDate: Date, toDate: Date) => {
    if (fromDate && toDate) {
      const formatedFromDate = moment(fromDate).format("MM.DD.YYYY")
      const formatedToDate = moment(toDate).format("MM.DD.YYYY")


      const reportName = `QAPP_${formatedFromDate}-${formatedToDate}.csv`
      setState({ reportName })

    }

    setPreviewQuery({
      fromDate: moment(state.reportDates[0]).utc().format(),
      toDate: moment(state.reportDates[1]).utc().format(),
      carWashIds: state.selectedBranchesForReports,
      statusIds: state.selectedStatusesForReports,
      boxIds: state.selectedBoxes,
      washerIds: state.selectedWashersForReports,
      reservationSourceId: state.selectedSourcesForReports
    })
  }




  const handleDownloadReport = () => {
    setQuery({
      fromDate: moment(state.reportDates[0]).utc().format(),
      toDate: moment(state.reportDates[1]).utc().format(),
      carWashIds: state.selectedBranchesForReports,
      statusIds: state.selectedStatusesForReports,
      boxIds: state.selectedBoxes,
      washerIds: state.selectedWashersForReports,
      reservationSourceId: state.selectedSourcesForReports
    })
  }


  const downloadReport = () => {
    const downloadUrl = window.URL.createObjectURL(new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), reportInfo], { type: "text/csv;charset=utf-8" }))
    //  const downloadUrl = window.URL.createObjectURL(new Blob([reportData], { type: "text/csv;charset=utf-8" }))
    const link = document.createElement("a")
    link.href = downloadUrl
    link.setAttribute("download", state.reportName)
    document.body.appendChild(link)
    link.click()
  }

  const clearReport = () => {
    setState({ reportName: "", reportDates: [] })
  }




  const updateSelectedBranchesForReports = (newBranches: number[]) => setState({ selectedBranchesForReports: newBranches })

  const handleStatusCheckboxClick = (statusId: number) => {
    let updatedSelectedStatuses = [...state.selectedStatusesForReports]


    if (updatedSelectedStatuses.includes(statusId)) {
      updatedSelectedStatuses = updatedSelectedStatuses.filter((selectedStatus: number) => selectedStatus !== statusId)

    } else {
      updatedSelectedStatuses.push(statusId)

    }

    setState({ selectedStatusesForReports: updatedSelectedStatuses })
    // setQuery(updatedQuery)
  }

  const handleWasherCheckboxClick = (washerId: number) => {
    let updatedSelectedWashers = [...state.selectedWashersForReports]


    if (updatedSelectedWashers.includes(washerId)) {
      updatedSelectedWashers = updatedSelectedWashers.filter((selectedWasher: number) => selectedWasher !== washerId)

    } else {
      updatedSelectedWashers.push(washerId)

    }

    setState({ selectedWashersForReports: updatedSelectedWashers })
    // setQuery(updatedQuery)
  }

  const handleSourcesCheckboxClick = (statusId: number) => {
    let updatedSelectedSources = [statusId]; // Only the current statusId should be in the array


    // If the statusId is already selected, unselect it
    if (state.selectedSourcesForReports.includes(statusId)) {
      updatedSelectedSources = [];

    } else {
      // Otherwise, select the new statusId and update the query accordingly

    }

    setState({ selectedSourcesForReports: updatedSelectedSources });
    // setQuery(updatedQuery);
  };


  const handleBoxCountCheckbox = (box: number) => {
    let updatedBoxes = [...state.selectedBoxes]


    if (updatedBoxes.includes(box)) {
      updatedBoxes = updatedBoxes.filter((selectedBox: number) => selectedBox !== box)

    } else {
      updatedBoxes.push(box)

    }

    setState({ selectedBoxes: updatedBoxes })
    // setQuery(updatedQuery)
  }

  useEffect(() => {
    setState({ reportStatuses: reportStatusesData?.data })
    setState({ reportSources: reportSourcesData?.data })
    setState({ reportBoxes: reportBoxesData?.data })
    setState({ reportWashers: reportWashersData?.data })
  }, [reportStatusesData, reportSourcesData, reportBoxesData, reportWashersData])

  useEffect(() => {
    if (lang && isReportSourcesFetched && isReportStatusesFetched) {
      refetchReportSources()
      refetchReportStatuses()
    }
  }, [lang])

  useEffect(() => {
    setReportInfo(reportData)
  }, [reportData])

  useEffect(() => {
    if (query.fromDate && query.toDate) {
      refetchReportData()
    }
  }, [query])

  useEffect(() => {
    if (previewQuery.fromDate && previewQuery.toDate) {
      refetchReportPreviewData()
    }
  }, [previewQuery])

  // useEffect(() => {
  //   if (reportDataFetched && (query.fromDate && query.toDate)) {
  //     refetchReportData()
  //   }
  // }, [query, reportDataFetched])

  useEffect(() => {

    if (reportInfo && (query.fromDate && query.toDate)) {
      downloadReport()
    }

    return () => {
      setReportInfo(undefined)
    }

  }, [reportInfo])




  const value = useMemo(() => ({
    ...state,
    ...query,
    handleDateChange,
    generateReport,
    clearReport,
    handleDownloadReport,
    updateSelectedBranchesForReports,
    handleStatusCheckboxClick,
    handleWasherCheckboxClick,
    handleSourcesCheckboxClick,
    handleBoxCountCheckbox
  }), [state])

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  )
}

export default ReportsProvider